import {
  ListItemButton,
  ListItemProps,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import type { CSSObject } from 'tss-react';
import { ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import React, { useRef } from 'react';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { useNavigate } from 'react-router-dom';
import { useStores } from '../../../stores';

export interface SideNavBarTileProps extends ListItemProps {
  startIcon?: React.ReactNode;
  label?: React.ReactNode;
  onClick?: () => void;
  isActive?: boolean;
  showDropDownIcon?: boolean;
  rootStyles?: CSSObject;
  labelTextStyles?: string;
  isOpen?: boolean;
  borderLeftString?: string;
  bgColorString?: string;
  currentState?: boolean;
  tabIndex?: number;
  handleActiveDropdownTab?: (_index: number) => void;
  menu?: any[];
  path?: string;
  menuIndex?: number;
  handleMenuOpen?: (_index: number) => void;
  handleMenuClose?: () => void;
}

const useStyles = makeStyles<{
  isOpen?: boolean;
  isActive?: boolean;
  bgColorString?: string;
  showDropDownIcon?: boolean;
  rootStyles?: CSSObject;
}>()(
  (_, { isOpen, isActive, bgColorString, showDropDownIcon, rootStyles }) => ({
    root: {
      height: '40px',
      width: isOpen ? '250px' : '64px',
      backgroundColor: isActive ? '#DADFF2' : bgColorString ?? 'inherit',
      '&:hover': {
        backgroundColor: patientAccessTheme.palette.navigationBar?.titleBg,
      },
      padding: isActive ? '8px 12px 8px 14px' : '8px 12px 8px 18px',
      ...rootStyles,
    },
    labelStyle: {
      fontWeight: showDropDownIcon && isActive ? 600 : 400,
      fontSize: '14px',
      color: patientAccessTheme.palette.navigationBar?.navTitle,
      lineHeight: '24px',
    },
    endIconStyle: {
      height: '20px',
      width: '20px',
      color: patientAccessTheme.palette.status.icon,
      cursor: 'pointer',
      flexShrink: 0,
    },
    activeIndicator: {
      height: '40px',
      width: '5px',
      borderRadius: '4px',
      marginLeft: '-14px',
      marginRight: '13px',
      flexShrink: 0,
    },
  }),
);

const SideNavBarTile: React.FC<SideNavBarTileProps> = ({
  startIcon,
  label,
  onClick,
  isActive,
  showDropDownIcon,
  rootStyles,
  labelTextStyles,
  isOpen,
  borderLeftString,
  bgColorString,
  currentState,
  tabIndex,
  handleActiveDropdownTab,
  menu,
  path,
  menuIndex,
  handleMenuClose,
  handleMenuOpen,
}) => {
  const pathStore = useStores().pathStore;
  const employerGroupStore = useStores().employerGroupStore;
  const navigate = useNavigate();

  const anchorRef = useRef<HTMLDivElement>(null);

  const { classes } = useStyles({
    isOpen,
    isActive,
    bgColorString,
    showDropDownIcon,
    rootStyles,
  });

  const handleDropdownIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (tabIndex !== undefined) handleActiveDropdownTab?.(tabIndex);
  };

  const handleTextMouseEnter = () => {
    if (tabIndex !== undefined) handleMenuOpen?.(tabIndex);
  };

  const handleTextClick = () => {
    if (path) {
      employerGroupStore?.removeSelectedDuration();
      pathStore.clearPath();
      navigate(path);
    }
  };

  const handleMenuItemClick = (route: string) => {
    navigate(route);
    handleMenuClose?.();
  };

  return (
    <div onClick={onClick}>
      <ListItemButton className={classes.root}>
        {isActive && (
          <div
            className={classes.activeIndicator}
            style={{ borderRight: borderLeftString ?? '' }}
          />
        )}

        {startIcon ?? null}

        {isOpen && (
          <div
            ref={anchorRef}
            style={{
              display: 'flex',
              flex: 1,
            }}
            onClick={handleTextClick}
          >
            <ListItemText
              id={
                tabIndex !== undefined ? `list-${tabIndex}` : 'list-item-text'
              }
              sx={{ margin: startIcon ? '0 0 0 12px' : '0 0 0 24px' }}
              onClick={handleTextClick}
              // onMouseEnter={handleTextMouseEnter}
              primary={
                <Typography className={labelTextStyles ?? classes.labelStyle}>
                  {label}
                </Typography>
              }
            />
          </div>
        )}

        {showDropDownIcon &&
          isOpen &&
          (currentState ? (
            <ArrowDropUp
              className={classes.endIconStyle}
              onClick={handleDropdownIconClick}
            />
          ) : (
            <ArrowDropDown
              className={classes.endIconStyle}
              onClick={handleDropdownIconClick}
            />
          ))}

        {menu && menu.length > 0 && (
          <Menu
            anchorEl={anchorRef.current}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            keepMounted
            open={menuIndex === tabIndex}
            onClose={handleMenuClose}
            slotProps={{
              paper: {
                sx: { marginLeft: '8px' },
              },
            }}
          >
            {menu.map((item: any, idx: number) => (
              <MenuItem key={idx} onClick={() => handleMenuItemClick(item[1])}>
                {item[0]}
              </MenuItem>
            ))}
          </Menu>
        )}
      </ListItemButton>
    </div>
  );
};

export default SideNavBarTile;
